<template>
    <CompassDialog
        :visible.sync="showModal"
        size="small"
        :title="title"
        class="popup-manager-payment-card"
        @closed="$emit('close')"
    >
        <template slot="default">
            <div class="dialog__default">
                <div v-if="validationError" class="alert alert-danger mb-3 mt-0">
                    {{ alertText }}
                </div>
                <el-form ref="cardForm" :model="modelForm" :rules="rules">
                    <p class="mb-3 typographies-paragraph-medium-regular">{{ subtitle }}</p>
                    <div class="flex-column card-inputs">
                        <!-- CARD NUMBER -->
                        <el-form-item
                            ref="cardNumber"
                            :class="{ disabled: validationProcessing }"
                            :error="errors.get('cardNumber')"
                            prop="cardNumber"
                        >
                            <p class="semibold pb-1">
                                {{ $l10n.__('Numéro de la carte') }}<span class="gray-60"> * </span>
                            </p>
                            <div
                                v-model="modelForm.cardNumber"
                                id="stripe-number-card"
                                class="card-number card-inputs"
                            />
                        </el-form-item>

                        <div class="flex-row" style="gap: 16px">
                            <!-- CARD EXPIRY -->
                            <el-form-item
                                ref="cardExpiry"
                                :class="{ disabled: validationProcessing }"
                                :error="errors.get('cardExpiry')"
                                class="flex-2"
                                prop="cardExpiry"
                            >
                                <p class="semibold pb-1">
                                    {{ $l10n.__("Date d'expiration") }}
                                    <span class="gray-60"> * </span>
                                </p>
                                <div id="stripe-expiration-card" class="card-inputs"></div>
                            </el-form-item>

                            <!-- CARD CVC -->
                            <el-form-item
                                ref="cardCvc"
                                :class="{ disabled: validationProcessing }"
                                :error="errors.get('cardCvc')"
                                class="flex-2"
                                prop="cardCvc"
                            >
                                <p class="semibold pb-1">
                                    {{ $l10n.__('CVC / CVV') }}<span class="gray-60"> * </span>
                                </p>
                                <div id="stripe-cvc-card" class="card-inputs"></div>
                            </el-form-item>
                        </div>
                        <el-form-item :class="{ disabled: validationProcessing }" prop="cardName">
                            <p class="semibold pb-1">
                                {{ $l10n.__('Nom sur la carte') }}<span class="gray-60"> * </span>
                            </p>
                            <div class="stripe-name-card card-inputs"></div>
                            <el-input v-model="modelForm.cardName" class="card-inputs"></el-input>
                        </el-form-item>

                        <p class="fs-2 gray-60 info-disclaimer">
                            {{
                                $l10n.__(
                                    'Nous conservons de manière sécurisée vos informations bancaires afin de procéder automatiquement au paiement de votre abonnement en fonction des modalités de paiement choisies.',
                                )
                            }}
                        </p>
                    </div>
                </el-form>
            </div>
        </template>

        <template slot="footer">
            <div class="dialog__footer">
                <div class="semibold fs-3 flex-row">
                    <loader
                        v-show="validationProcessing"
                        class="mr-2"
                        loaderColor="black"
                        position="relative"
                    ></loader>
                    <span v-show="validationProcessing">
                        {{ $l10n.__('Vérification en cours') }}
                    </span>
                </div>
                <div class="container-button">
                    <CompassButton
                        :label="$t.__('common.actions.cancel')"
                        type="secondary"
                        @click="$emit('close')"
                    />
                    <CompassButton :label="textBtnSubmit" type="primary" @click="onSubmitForm()" />
                </div>
            </div>
        </template>
    </CompassDialog>
</template>

<script>
import IconCardCVC from 'vcomponents/icons/IconCardCVC.vue';
import IconCardDefault from 'vcomponents/icons/IconCardDefault.vue';
import IconSvg from 'vcomponents/icons/IconSvg.vue';
import Loader from 'vcomponents/utilities/Loader.vue';
import { getErrorInput, getErrorLabel } from 'api/sellsyaccountmanager';

import SAMMixins from 'mixins/subscription/SAMMixins';
import FormMixins from 'mixins/FormMixins';
import { checkIsTrue } from 'vcomponents/forms/rules/Rules';
import StripeSellsy from 'Core/commons/StripeSellsy';
import Env from 'Services/Env';
import Errors from 'Core/commons/Errors';
import { createNamespacedHelpers } from 'vuex';

const corpMapState = createNamespacedHelpers('corp').mapState;

export default {
    components: {
        IconCardCVC,
        IconCardDefault,
        IconSvg,
        Loader,
    },
    mixins: [FormMixins, SAMMixins],
    props: {
        mode: {
            type: String,
            required: true,
            default: 'edit',
        },
    },
    data() {
        return {
            showModal: true,
            publishableKey: Env.get('stripe').publishableKey,
            stripe: null,
            modelForm: {
                cardNumber: false,
                cardExpiry: false,
                cardCvc: false,
                cardName: null,
            },
            rules: {
                cardNumber: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner un numéro de carte valide'),
                    },
                ],
                cardExpiry: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner une date valide'),
                    },
                ],
                cardCvc: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner un code valide'),
                    },
                ],
                cardName: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner le nom sur la carte'),
                    },
                ],
            },
            errors: new Errors(),
            validationError: false,
            validationProcessing: false,
        };
    },
    computed: {
        ...corpMapState(['id']),
        editMode() {
            return this.mode === 'edit';
        },
        title() {
            if (this.editMode) {
                return this.$l10n.__('Modifier mon moyen de paiement');
            } else {
                return this.$l10n.__('Ajouter mon moyen de paiement');
            }
        },
        subtitle() {
            if (this.editMode) {
                return this.$l10n.__('Saisissez les informations de votre carte bancaire.');
            } else {
                return this.$l10n.__(
                    'Saisissez les informations de votre nouvelle carte bancaire.',
                );
            }
        },
        textBtnSubmit() {
            if (this.editMode) {
                return this.$l10n.__('Modifier');
            } else {
                return this.$l10n.__('Ajouter');
            }
        },
        alertText() {
            return this.$l10n.__(
                'La vérification a échouée. Corrigez les informations de cette carte bancaire ou utilisez en une autre.',
            );
        },
    },
    created() {
        this.fetchCurrentCreditCard();
        this.stripe = new StripeSellsy();
    },
    events: {
        'stripe:error': function (payload) {
            const error = payload.params[0];
            this.errors.setOneError(error.type, error.message);
        },
        'stripe:clear': function (payload) {
            const event = payload.params[0];
            this.resetCard(event.type, event.event.complete);
        },
    },
    methods: {
        onSubmitForm() {
            this.validationError = false;
            this.validationProcessing = true;
            this.error = null;
            this.stripe
                .createToken({ name: this.modelForm.cardName })
                .then((response) => {
                    this.recordStripeToken(this.id, response.token.id)
                        .then(() => {
                            this.$emit('close');
                            this.$trigger('payment:cardSaved');
                            this.hideBanner();
                        })
                        .catch((error) => {
                            this.errors.setOneError(
                                getErrorInput(error.error),
                                getErrorLabel(error.error),
                            );
                            Push.error(getErrorLabel(error.error));
                        })
                        .finally(() => {
                            this.validationProcessing = false;
                            this.validationError = false;
                        });
                    if (response.error) {
                        this.validationError = true;
                        throw new Error(response.error);
                    }
                })
                .catch(() => {
                    this.validationProcessing = false;
                    this.validationError = true;
                });
        },
        /**
         * Clear the last saved string for stripe verification then submit the new input
         * type (string) : name of stripe input
         * complete (boolean) : false if field isn't valid
         */
        resetCard(type, complete) {
            this.errors.clear(type);
            switch (type) {
                case 'cardNumber':
                    this.modelForm.cardNumber = complete;
                    break;
                case 'cardExpiry':
                    this.modelForm.cardExpiry = complete;
                    break;
                case 'cardCvc':
                    this.modelForm.cardCvc = complete;
                    break;
            }
        },
        hideBanner() {
            const subscribebar = document.querySelector('.subscribebar');
            const navbar = document.querySelector('#sellsy_app .navbar');
            if (subscribebar) {
                subscribebar.classList.add('transitionHiddenBanner');
                navbar.classList.add('headerTransition');
                document.querySelector('#sellsy_app #menu').classList.add('menuTransition');
                document.querySelector('#sellsy_app #contentDiv').classList.add('menuTransition');
            }
        },
        fetchCurrentCreditCard() {
            this.loading = true;
            this.getActiveCreditCard(this.id)
                .then((response) => {
                    this.currentCreditCard = response;
                    if (this.currentCreditCard.name) {
                        this.modelForm.cardName = this.currentCreditCard.name;
                    }
                })
                .catch((error) => {
                    if (error.code !== 404) {
                        Push.error(
                            this.$l10n.__(
                                'Une erreur est survenue lors de la récupération de vos informations de paiement',
                            ),
                        );
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.popup-manager-payment-card {
    .dialog {
        &__default {
            padding-bottom: 24px;
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            justify-content: space-between;
            padding: 0 32px 24px 32px;
        }
    }

    span {
        line-height: 20px;
    }
    p {
        margin-top: 0;
        margin-bottom: 0;
    }

    .flex-column {
        overflow-y: hidden;

        label div {
            margin-top: 4px;
        }
    }

    .container-button {
        display: flex;
        gap: 14px;
    }

    .disabled {
        .el-form-item__content {
            cursor: not-allowed !important;

            .card-inputs,
            .el-input__inner {
                color: $gray-60 !important;
                background-color: $gray-05 !important;
                border-color: $gray-40 !important;
                pointer-events: none !important;
            }
        }
    }

    //stripe
    .card-number .CardNumberField.CardNumberField--ltr .CardBrandIcon-container {
        right: 0px !important;
    }
    .CardNumberField-input-wrapper {
        position: initial;
    }
}
</style>
